import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Lock, RefreshCw, Unlock, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Facility {
  id: string;
  name: string | null;
  address: string | null;
  isPrivateFacility: boolean | null;
  doctorScheduleSyncEnabled: boolean | null;
}

interface EditFacilityDialogProps {
  facility: Facility | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (facilityId: string, data: { address?: string; isPrivateFacility?: boolean }) => void;
  isSaving?: boolean;
}

export function EditFacilityDialog({
  facility,
  open,
  onOpenChange,
  onSave,
  isSaving = false,
}: EditFacilityDialogProps) {
  const [address, setAddress] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);

  // Reset form when facility changes or dialog opens
  useEffect(() => {
    if (facility && open) {
      setAddress(facility.address || '');
      setIsPrivate(facility.isPrivateFacility || false);
    }
  }, [facility, open]);

  const handleSave = () => {
    if (!facility) return;

    const updates: { address?: string; isPrivateFacility?: boolean } = {};

    // Only include fields that changed
    if (address !== (facility.address || '')) {
      updates.address = address;
    }

    if (isPrivate !== (facility.isPrivateFacility || false)) {
      updates.isPrivateFacility = isPrivate;
    }

    onSave(facility.id, updates);
  };

  // Check if there are any changes
  const hasChanges =
    address !== (facility?.address || '') || isPrivate !== (facility?.isPrivateFacility || false);

  if (!facility) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Facility</DialogTitle>
          <DialogDescription>
            Update facility address and privacy settings for{' '}
            <span className="font-medium">{facility.name || 'Unnamed Facility'}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Address Field */}
          <div className="grid gap-2">
            <Label htmlFor="address">Address</Label>
            <Textarea
              id="address"
              placeholder="Enter facility address..."
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Physical address of the facility (optional)
            </p>
          </div>

          {/* Privacy Setting */}
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="private-switch" className="flex items-center gap-2">
                {isPrivate ? (
                  <>
                    <Lock className="h-4 w-4" />
                    Private Facility
                  </>
                ) : (
                  <>
                    <Unlock className="h-4 w-4" />
                    Public Facility
                  </>
                )}
              </Label>
              <Switch id="private-switch" checked={isPrivate} onCheckedChange={setIsPrivate} />
            </div>
            <p className="text-xs text-muted-foreground">
              Private facilities (surgery centers) don't share appointment details with patients
            </p>
          </div>

          {/* Schedule Sync Status (Read-Only) */}
          <div className="grid gap-2">
            <Label>Schedule Sync Status</Label>
            <div className="flex items-center gap-2">
              <Badge
                variant={facility.doctorScheduleSyncEnabled ? 'default' : 'secondary'}
                className="flex items-center gap-1"
              >
                {facility.doctorScheduleSyncEnabled ? (
                  <>
                    <RefreshCw className="h-3 w-3" />
                    Sync Enabled
                  </>
                ) : (
                  <>
                    <X className="h-3 w-3" />
                    Sync Disabled
                  </>
                )}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {facility.doctorScheduleSyncEnabled
                ? 'This facility syncs doctor schedules from the EHR system'
                : 'Schedule sync is disabled for this facility'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaving || !hasChanges}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
